import React, { Fragment } from 'react';
import ReactDOM from 'react-dom';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';

import GrantsFilters from 'components/grantsFilters';
import GrantsList from 'components/grantsList';

import makeTaxonomyMap from '../../js/services/makeTaxonomyMap';
import searchGrantFields from '../../js/services/searchGrantFields';

import './grants.scss';
import stripTags from '../../js/utils/stripTags';
import groupGrantsByStrategy from '../../js/services/groupGrantsByStrategy';

const appElement = document.querySelector('.grants-app');

class Grants extends React.Component {
  /**
   * Determines if a set of filter values matches grant attributes.
   * @param {string|array} filters - filter values.
   * @param {object} grant - a single grant.
   * @param {string} field - the field to investigate.
   * @returns {boolean}
   */
  static filterField(filters, grant, field) {
    return filters.map((filter) => (
      ('object' === typeof grant[field]) ?
        grant[field].includes(filter) :
        parseInt(grant[field], 10) === filter
    )).some((item) => item);
  }

  /**
   * Constructor. Binds function scope.
   * @param {object} props - Props for this component.
   */
  constructor(props) {
    super(props);
    this.state = {
      activeType: [],
      activeProgram: [],
      activeSearch: [],
      activeState: [],
      activeStrategy: [],
      activeYear: [],
      displayCount: 10,
      incrementAmount: 5,
      grants: undefined,
      grantTypesTaxonomy: [],
      loading: true,
      programAreasTaxonomy: undefined,
      statesTaxonomy: [],
      sortDirection: 1,
      sortField: 'organization',
      strategiesTaxonomy: undefined,
      years: undefined,
      filtersLoaded: false,
    };
    this.loadFromHash = this.loadFromHash.bind(this);
    this.clearFilters = this.clearFilters.bind(this);
    this.handleSortClick = this.handleSortClick.bind(this);
    this.handleOrderClick = this.handleOrderClick.bind(this);
    this.loadMore = this.loadMore.bind(this);
    this.onFilter = this.onFilter.bind(this);
    this.removeFilter = this.removeFilter.bind(this);
    this.filteredGrants = {};
  }

  componentDidMount() {
    this.mounted = true;
    let totalGrantPages;
    // Get program areas taxonomy
    this.fetchProgramAreas = apiFetch({
      path: '/wp-json/wp/v2/program-areas?per_page=100',
    })
      .then((data) => {
        this.setState({
          programAreasTaxonomy: makeTaxonomyMap(data),
        });
      }).catch(() => {
        this.setState({
          programAreasTaxonomy: [],
        });
      });
    // Get states taxonomy
    apiFetch({
      path: '/wp-json/wp/v2/states?per_page=100',
    })
      .then((data) => {
        this.setState({
          statesTaxonomy: makeTaxonomyMap(data.filter((item) => item.count)),
        });
      }).catch(() => {
        this.setState({
          statesTaxonomy: [],
        });
      });
    // Get strategies taxonomy
    this.fetchStrategies = apiFetch({
      path: '/wp-json/wp/v2/strategies?per_page=100',
    })
      .then((data) => {
        this.setState({
          strategiesTaxonomy: makeTaxonomyMap(data),
        });
      }).catch(() => {
        this.setState({
          strategiesTaxonomy: [],
        });
      });
    // Get grant-types taxonomy
    this.fetchGrantTypes = apiFetch({
      path: '/wp-json/wp/v2/grant-types?per_page=100',
    })
      .then((data) => {
        this.setState({
          grantTypesTaxonomy: makeTaxonomyMap(data),
        });
      }).catch(() => {
        this.setState({
          grantTypesTaxonomy: [],
        });
      });
    // Get grants data
    this.fetchGrants = apiFetch({
      path: '/wp-json/wp/v2/grant?page=1&per_page=100&orderby=id&order=asc',
      parse: false,
    })
      .then((response) => {
        totalGrantPages = parseInt(response.headers.get('X-WP-TotalPages'), 10);

        return response.json();
      })
      .then((data) => {
        this.setGrantsData(data);

        // Get the rest of the data
        [...Array(totalGrantPages - 1).keys()].forEach((page) => {
          apiFetch({
            path: `/wp-json/wp/v2/grant?per_page=100&page=${page + 2}&orderby=id&order=asc`, // eslint-disable-line max-len
          })
            .then((grants) => {
              this.setGrantsData(grants);
            });
        });
        this.loadFromHash();
      }).catch(() => {
        if (this.mounted) {
          this.setState({
            grants: [],
            loading: false,
            years: [],
          });
        }
      });
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  /**
   * Update state on filter.
   * @param {string} type - The filter that has been changed.
   * @param {string} value - The new filter value.
   */
  onFilter(type, value) {
    this.setState({
      [`active${type}`]:
      Array.from(new Set([value, ...this.state[`active${type}`]])),
    });
  }

  /**
   * Update existing app state with incoming REST data
   * @param {object} data - Incoming grants data
   */
  setGrantsData(data) {
    const {
      grants = [],
      years = [],
    } = this.state;

    const formattedGrants = this.formatGrantsData(data);
    const uniqueYears =
      Array.from(new Set(formattedGrants.map((grant) =>
        grant.year.toString())));
    this.setState({
      grants: [...new Set([
        ...grants,
        ...formattedGrants,
      ])],
      loading: false,
      years: [...new Set([
        ...years,
        ...uniqueYears,
      ])].sort().reverse(),
    });
  }

  pushToHash() {
    if (! this.state.filtersLoaded) {
      return;
    }
    const attr = document.querySelector('.grants-app').dataset.filter;
    if (attr) {
      return;
    }
    const {
      activeType,
      activeSearch,
      activeState,
      activeYear,
      activeStrategy,
    } = this.state;
    const query = {};
    if (activeType.length) {
      query.grantType = activeType.join(',');
    }
    if (activeState.length) {
      query.state = activeState.join(',');
    }
    if (activeYear.length) {
      query.year = activeYear.join(',');
    }
    if (activeSearch.length) {
      query.search = activeSearch.join(',');
    }
    if (activeStrategy.length) {
      query.strategy = activeStrategy.join(',');
    }
    window.history.pushState(
      query,
      'Grants',
      `#${new URLSearchParams(query).toString()}`,
    );
  }

  loadFromHash() {
    const attr = document.querySelector('.grants-app').dataset.filter;
    const query = attr ? new URLSearchParams(attr) : new URLSearchParams(window.location.hash.slice(1));
    if (query.has('grantType')) {
      this.setState({
        activeType: query.get('grantType').split(',').map((grantType) => parseInt(grantType, 10)),
      });
    }
    if (query.has('state')) {
      this.setState({
        activeState: query.get('state').split(',').map((state) => parseInt(state, 10)),
      });
    }
    if (query.has('year')) {
      this.setState({
        activeYear: query.get('year').split(',').map((year) => parseInt(year, 10)),
      });
    }
    if (query.has('search')) {
      this.setState({
        activeSearch: query.get('search').split(','),
      });
    }
    if (query.has('strategy')) {
      this.setState({
        activeStrategy: query.get('strategy').split(',').map((strategy) => parseInt(strategy, 10)),
      });
    }
    this.setState({ filtersLoaded: true });
  }

  /**
   * Clears all filters
   */
  clearFilters() {
    this.setState({
      activeType: [],
      activeProgram: [],
      activeSearch: [],
      activeState: [],
      activeStrategy: [],
      activeYear: [],
    });
  }

  /**
   * Remove filter condition.
   * @param {string} type - The filter that has been changed.
   * @param {string} value - The filter to remove.
   */
  removeFilter(type, value) {
    const newFilters =
      this.state[`active${type}`].filter((item) => item !== value);
    this.setState({
      [`active${type}`]: newFilters,
    });

    /**
     * If the user is removing the "program",
     * we need to reset the "stategy" as well 🙄.
     */
    if ('Program' === type && ! newFilters.length) {
      this.setState({ activeStrategy: [] });
    }
  }

  /**
   * Filters out orphaned terms and returns the results.
   * @param {object} terms All of the terms from a given taxonomy.
   * @param {string} taxSlug The slug of a given taxonomy.
   */
  filterTerms(terms, taxSlug) {
    const { activeProgram, grants } = this.state;

    // If there are no programs set. Return all strategies.
    if (! activeProgram.length) {
      return terms;
    }

    // eslint-disable-next-line max-len
    const grantsByProgram = grants.filter(({ programs }) => programs.includes(activeProgram[0]));
    const taxArrays = grantsByProgram.map((grant) => grant[taxSlug]);
    const uniqueIds = [...new Set(taxArrays.flat(1))];

    //  If we are working with years, we're done.
    if ('year' === taxSlug) {
      return uniqueIds;
    }

    const filteredTerms = {};
    Object.keys(terms).forEach((key) => {
      const searchVal = terms[key].id || terms[key];

      if (uniqueIds.includes(searchVal)) {
        filteredTerms[key] = terms[key];
      }
    });

    return filteredTerms;
  }

  /**
   * Formats data from the API and applies taxonomy info.
   * @param {array} grantsData - The grant data from the API.
   * @returns {array} - an array of formatted grants.
   */
  formatGrantsData(grantsData) { // eslint-disable-line class-methods-use-this
    const grantsList = grantsData.map((grant) => {
      const {
        id: id = 0,
        states,
        title: {
          rendered: name = '',
        } = {},
        content: {
          rendered: description = '',
        },
        'grant-types': grantTypes = [],
        strategies = [],
        'program-areas': programs = [],
        meta: {
          kauffman_grant_approved_amount: amount = 0,
          kauffman_grant_approved_on: date = '',
          kauffman_grant_description: metaDescription = '',
          kauffman_grant_city: location = '',
          kauffman_grant_url: url = '',
          kauffman_grant_type: type = '',
        } = {},
      } = grant;
      return {
        amount,
        date,
        description: metaDescription || stripTags(description),
        grantTypes,
        id,
        location,
        name,
        programs,
        states,
        strategies,
        url,
        type,
        year: date ? new Date(date).getFullYear().toString() : '',
      };
    });
    return grantsList;
  }

  /**
   * Increments the number of displayed grants.
   */
  loadMore() {
    const newDisplayCount =
      this.state.displayCount + this.state.incrementAmount;
    this.setState({
      displayCount: newDisplayCount,
    });
  }

  /**
   * Applies active filters and sorting to the grants list.
   * @param {array} grants - list of all grants.
   * @returns {array} - The ordered list of grants.
   */
  filterAndSort(grants) {
    const {
      activeType,
      activeProgram,
      activeSearch,
      activeState,
      activeStrategy,
      activeYear,
      sortDirection,
      sortField,
      strategiesTaxonomy,
    } = this.state;

    // Deep copy to avoid side effects
    const grantsCopy = grants.map((grant) => ({ ...grant }));
    // Apply filters
    const filteredGrants = grantsCopy.filter((grant) => (
      (activeType.length ?
        Grants.filterField(activeType, grant, 'grantTypes') : true) &&
      (activeProgram.length ?
        Grants.filterField(activeProgram, grant, 'programs') : true) &&
      (activeStrategy.length ?
        Grants.filterField(activeStrategy, grant, 'strategies') : true) &&
      (activeState.length ?
        Grants.filterField(activeState, grant, 'states') : true) &&
      (activeYear.length ?
        Grants.filterField(activeYear, grant, 'year') : true) &&
      (activeSearch.length ?
        searchGrantFields(grant, activeSearch) : true)
    ));

    // Sort grants
    if ('strategy' === sortField) { // By strategy
      // eslint-disable-next-line max-len
      return groupGrantsByStrategy(filteredGrants, strategiesTaxonomy, sortDirection);
    } else if ('organization' === sortField) {
      return filteredGrants.sort((grantA, grantB) => (
        (grantA.name.localeCompare(grantB.name)) * sortDirection
      ));
    } else if ('date' === sortField) { // By award date
      return filteredGrants.sort((grantA, grantB) => (
        (new Date(grantA.date) - new Date(grantB.date)) * sortDirection
      ));
    }
    // By amount or numeric value
    return filteredGrants.sort((grantA, grantB) => (
      (grantA[sortField] - grantB[sortField]) * sortDirection
    ));
  }

  /**
   * Applies sort on table header click.
   * @param {string} type - The column to sort.
   */
  handleSortClick(type) {
    this.setState({
      sortField: type,
    });
  }

  /**
   * Applies sort on table header click.
   * @param {string} type - The column to sort.
   */
  handleOrderClick() {
    const {
      sortDirection,
    } = this.state;
    let newDirection = 1;
    newDirection = (- 1 === sortDirection) ? 1 : - 1;
    this.setState({
      sortDirection: newDirection,
    });
  }

  /**
   * Renders component markup.
   * @returns {object} - JSX for this component.
   */
  render() {
    const {
      activeType,
      activeProgram,
      activeSearch,
      activeState,
      activeStrategy,
      activeYear,
      displayCount,
      grants,
      grantTypesTaxonomy,
      incrementAmount,
      loading,
      programAreasTaxonomy,
      sortDirection,
      sortField,
      statesTaxonomy,
      strategiesTaxonomy,
      years,
    } = this.state;
    this.pushToHash();

    if (! grants || ! programAreasTaxonomy || ! strategiesTaxonomy) {
      return false;
    }

    if (! appElement) {
      return false;
    }

    if (0 === grants.length ||
      0 === programAreasTaxonomy ||
      0 === strategiesTaxonomy) {
      return false;
    }

    // Apply filters and sorting to grants list
    this.filteredGrants = this.filterAndSort(grants);
    const strategiesFiltered =
      this.filterTerms(strategiesTaxonomy, 'strategies');
    const statesFiltered = this.filterTerms(statesTaxonomy, 'states');
    const programsFiltered = this.filterTerms(programAreasTaxonomy, 'programs');
    const yearsFiltered = this.filterTerms(years, 'year');
    const grantTypesFiltered = this.filterTerms(grantTypesTaxonomy, 'grantTypes');

    const { hideFilters } = document.querySelector('.grants-app').dataset;

    return (
      <div className="grant-listing">
        {loading ? (
          <div className="grant-listing__loading">
            <div>{__('Loading...', 'kauffman')}</div>
          </div>
        ) : (
          <Fragment>
            {'true' === hideFilters ? null : (
              <GrantsFilters
                activeType={activeType}
                activeProgram={activeProgram}
                activeSearch={activeSearch}
                activeState={activeState}
                activeStrategy={activeStrategy}
                activeYear={activeYear}
                clearFilters={this.clearFilters}
                grantTypesTaxonomy={grantTypesTaxonomy}
                grantTypesFiltered={grantTypesFiltered}
                onFilter={this.onFilter}
                programAreasTaxonomy={programAreasTaxonomy}
                programsFiltered={programsFiltered}
                removeFilter={this.removeFilter}
                strategiesTaxonomy={strategiesTaxonomy}
                strategiesFiltered={strategiesFiltered}
                statesTaxonomy={statesTaxonomy}
                statesFiltered={statesFiltered}
                years={years}
                yearsFiltered={yearsFiltered}
              />
            )}
            <GrantsList
              displayCount={displayCount}
              grants={this.filteredGrants}
              grantTypesTaxonomy={grantTypesTaxonomy}
              statesTaxonomy={statesTaxonomy}
              handleSortClick={this.handleSortClick}
              handleOrderClick={this.handleOrderClick}
              increment={incrementAmount}
              loadMore={this.loadMore}
              programAreasTaxonomy={programAreasTaxonomy}
              sortDirection={sortDirection}
              sortField={sortField}
              strategiesTaxonomy={strategiesTaxonomy}
            />
          </Fragment>
        )}
      </div>
    );
  }
}

if (appElement) {
  ReactDOM.render(React.createElement(Grants, null), appElement);
}
